import {get} from './object';
import {types} from './types';

/**
 * @name filterObjectsByKeyValues
 * @desc Returns a filtered array of objects; check if a keyPath/value in each object matches allowed value(s)
 * @param {Array} objs
 * @param {String} keyPath
 * @param {Array|String|Function} values
 * @returns {Array}
 * @memberof utils
 * @example
 * const oItems = [
 *   {foo: {bar:1}},
 *   {foo: {bar:1}},
 *   {foo: {bar:2}},
 *   {foo: {bar:3}}
 * ];
 *
 * // filter objects by a specific value at a key path
 * const items = filterObjectsByKeyValues(oItems, 'foo.bar', 1);
 * console.log(items.length); // 2
 * console.log(items); // [{foo: {bar:1}}, {foo: {bar:1}}]
 *
 * // filter objects by a multiple values at a key path
 * const items = filterObjectsByKeyValues(oItems, 'foo.bar', [1, 3]);
 * console.log(items.length); // 3
 * console.log(items); // [{foo: {bar:1}}, {foo: {bar:1}}, , {foo: {bar:3}}]
 *
 * // filter objects by a selector fn
 * const select = value => value === 3;
 * const items = filterObjectsByKeyValues(oItems, 'foo.bar', select);
 * console.log(items.length); // 1
 * console.log(items); // [{foo: {bar:3}}]
 *
 * // filter objects value is an object, null or undefined; items === oItems
 * const items = filterObjectsByKeyValues(oItems, 'foo.bar', {});
 * console.log(items.length); // 4
 */
export function filterObjectsByKeyValues(objs, keyPath, values) {
  // if allowed values is an array, the keyPath value of each obj must match one of those "values" in the array
  const type = types(values);
  if (isArrayNotEmpty(values)) {
    return objs.filter(obj => values.indexOf(get(obj, keyPath)) !== -1);
  // if allowed value is string, number, or boolean, the keyPath value of each obj must exactly match "values"
  } else if (type.isString || type.isNumber || type.isBoolean) {
    return objs.filter(obj => get(obj, keyPath) === values);
  // if allowed value is a function, the function determines if the keyPath value of each obj is a match
  } else if (type.isFunction) {
    return objs.filter(obj => values(get(obj, keyPath)));
  // else do nothing and just return the array of objects passed in
  } else {
    return objs;
  }
}

/**
 * @name filterObjectsByMultipleKeyValues
 * @desc Returns a filtered array of objects; check if MULTIPLE keyPath/value in each object matches allowed value(s)
 * @param {Array} objs
 * @param {Array} filters
 * @memberof utils
 * @example
 * const oItems = [
 *   {foo: {bar:1}},
 *   {foo: {bar:1}, abc:'xyz'},
 *   {foo: {bar:2}},
 *   {foo: {bar:3}}
 * ];
 * const filters = [
 *  {keyPath:'foo.bar', values:1},
 *  {keyPath:'foo.abc', values:['xyz', 'lmnop']}
 * ];
 * const items = filterObjectsByMultipleKeyValues(oItems, filters);
 * console.log(items.length); // 1
 * console.log(items); // [{foo: {bar:1}, abc:'xyz'}]
 */
export function filterObjectsByMultipleKeyValues(objs, filters) {
  if (isArrayNotEmpty(filters)) {
    return filters.reduce((prevObjs, filter) => {
      const {values} = filter;
      if (typeof values === 'undefined' || (Array.isArray(values) && values.length === 0)) {
        return prevObjs;
      } else {
        return filterObjectsByKeyValues(prevObjs, filter.keyPath, values);
      }
    }, objs);
  } else {
    return objs;
  }
}

/**
 * @name isArrayNotEmpty
 * @desc Check if argument is array with length
 * @param {Array} array
 * @returns {Boolean}
 * @memberof utils
 */
export function isArrayNotEmpty(array) {
  return Array.isArray(array) && array.length !== 0;
}

/**
 * @name sortArrayOfObjectsByKey
 * @desc Sort an array of objects by a key value
 * @param {Array} array
 * @param {String} key
 * @param {Boolean} [desc = false]
 * @returns {Array}
 * @memberof utils
 */
export function sortArrayOfObjectsByKey(array, key, desc = false) {
  const direction = desc ? -1 : 1;
  const sorter = (a, b) => direction * (a[key] > b[key] ? 1 : -1);
  return array.sort(sorter);
}

/**
 * @name sortArrayOfObjectsByKeyPath
 * @desc Sort an array by ascending or descending order
 * @param {Array} array immutable array
 * @param {Array} keyPath immutable path
 * @param {Boolean} [desc = false]
 * @param {Boolean} [isNumber = false]
 * @returns {Array}
 * @memberof utils
 */
export function sortArrayOfObjectsByKeyPath(array, keyPath, desc = false, isNumber = false) {
  const direction = desc ? -1 : 1;
  return array.sort((a, b) => {
    const value = isNumber ? Number(a.getIn(keyPath)) : a.getIn(keyPath);
    const nextValue = isNumber ? Number(b.getIn(keyPath)) : b.getIn(keyPath);
    return direction * (value > nextValue ? 1 : -1);
  });
}
